import { Link, useNavigate } from "react-router-dom";
import { SortOrderDropDownSelection } from "../../../components/CustomComponents/DropdownSelections/DropDownSelections";
import DropDownStyles, { DropDownStylesDark } from "../../staff/dropdownStyles";
import ColumnSelection from "../../../components/CustomComponents/ColumnSelection/ColumnSelection";
import InlinePageSizeSelector from "../../../components/CustomComponents/InlinePageSizeSelector/InlinePageSizeSelector";
import Pagination from "../../../components/CustomComponents/Pagination/pagination";
import { useCallback, useEffect, useState, useRef } from "react";
import useDarkMode from '../../../hooks/useDarkMode';
import useThemeColor from "../../../hooks/useThemeColor";
import Select from "react-select";
import { useSelector, useDispatch } from "react-redux";
import { setVisibleColumns, setSortOrder, setSortBy } from "../../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../../utils/preferencesUtils";
import { MANAGE_PAGE_SEARCH_DEBOUNCE_MS } from "../../../utils/managePageConstants";
import APIs from "../../../Constants/apis";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import Preloader from "../../../components/CustomComponents/preLoad/preLoader";
import showErrorToast, {
  showSuccessToast,
} from "../../../components/CustomComponents/toast/toast";
import swal from "sweetalert";
import { appSettings } from "../../../app/appSettingsSlice";
import "../../../components/CustomComponents/footer/footer.css";
import "../../order/ManageOrderFilters.css";
import { useProfileAndSettings } from "../../../hooks/useProfileAndSettings";

const ManageCustomers = () => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const [search, setSearch] = useState("");
  const pageKey = "customers";
  const defaultVisibleColumns = {
    email: true,
    mobileNo: true,
    city: true,
    postcode: true,
    // Additional columns from customer report (hidden by default)
    phone: false,
    address: false,
    country: false,
    totalOrders: false,
    totalSpent: false,
    averageOrderSpent: false,
    lastOrderDate: false,
    currentBalance: true,
    lastOrderType: false,
    totalEarnedLoyaltyPoints: false,
    totalSpentLoyaltyPoints: false,
    deposit: true,
    maxCredit: true,
    referenceID: false,
    vatNumber: false,
    businessName: false,
    dateOfBirth: false,
    gender: false,
    title: false,
  };
  
  const defaultSortOrder = { value: "ASC", label: "ASC" };
  const defaultSortBy = { value: "Cust_Guid", label: "GUID" };
  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const dispatch = useDispatch();

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  
  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  // Get theme color from centralized hook
  const themeColor = useThemeColor();
  
  // Get dynamic currency
  const { getCompCurrency } = useProfileAndSettings();
  const compCurrency = getCompCurrency();
  
  // Currency symbols mapping
  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };
  
  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [pageSize, setPageSize] = useState({ value: "50", label: "50" });
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const lastSelectedIndex = useRef(null);
  const navigate = useNavigate();

  const sortByOptions = [
    { value: "Cust_Guid", label: "GUID" },
    { value: "Cust_Fname", label: "Name" },
  ];

  const clearFilters = () => {
    setSearch("");
    setPageNumber(1);
  };

  const DeleteCustomer = async (customer) => {
    const confirmDelete = await swal({
      title: "Are you sure?",
      text: `Are you sure you want to delete ${customer.cust_Fname + " " + customer.cust_Lname}?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirmDelete) {
      return;
    }

    setLoading(true);
    try {
      await axiosInstance
        .delete(APIs.DELETE_CUSTOMER_BY_GUID, {
          params: {
            CompID,
            Cust_Guid: customer.cust_Guid,
          },
        })
        .then((response) => {
          if (response.data.status) {
            setCustomers((prevGroups) =>
              prevGroups.filter((delGroup) => delGroup.cust_Guid !== customer.cust_Guid)
            );
            showSuccessToast("Customer is Deleted.");
          } else showErrorToast(response.data.message);
        });
      setLoading(false);
    } catch (err) {
      showErrorToast("Something went wrong.");
      setLoading(false);

    }
  };

  const handleSelectItem = (customerId, index, event) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);

      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        for (let i = start; i <= end; i++) {
          if (customers[i]?.cust_Guid) {
            newSelected.add(customers[i].cust_Guid);
          }
        }
      } else {
        if (newSelected.has(customerId)) {
          newSelected.delete(customerId);
        } else {
          newSelected.add(customerId);
        }
        lastSelectedIndex.current = index;
      }

      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(customers.map((item) => item.cust_Guid));
      setSelectedItems(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
    lastSelectedIndex.current = null;
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      showErrorToast("No customers selected for deletion.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${selectedItems.size} selected customers?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedItems).map((customerId) =>
          axiosInstance.delete(APIs.DELETE_CUSTOMER_BY_GUID, {
            params: {
              CompID,
              Cust_Guid: customerId,
            },
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      fetchCustomers();
      showSuccessToast(`${selectedItems.size} customers deleted successfully.`);
      setLoading(false);
    } catch (err) {

      showErrorToast("Failed to delete selected customers.");
      setLoading(false);
    }
  };

  const handleBulkEdit = () => {
    if (selectedItems.size === 0) {
      showErrorToast("No customers selected for editing.");
      return;
    }

    const selectedCustomerIds = Array.from(selectedItems);
    navigate(`/customer/bulkEdit`, { state: { selectedCustomerIds } });
  };

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_CUSTOMERS, {
        params: {
          CompID,
          Search: search?.trim() || "",
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
          PageNumber: pageNumber,
          PageSize: pageSize.value,
        },
      });

      const customersData = response.data.data || response.data;

      setTotalPages(response.data.totalPages || 0);
      setTotalRecords(response.data.totalRecords || 0);
      
      if (Array.isArray(customersData)) {
        setCustomers(customersData);
      } else {
        setCustomers([]);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setCustomers([]);
      showErrorToast("Failed to fetch customers.");
    }
  }, [CompID, search, sortBy, sortOrder, pageNumber, pageSize]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchCustomers();
    }, MANAGE_PAGE_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [fetchCustomers]);

  useEffect(() => {
    // Check if preferences exist and merge with defaults to ensure new columns are included
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      // Check if preferences need updating (missing new columns or have removed columns)
      if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
        // Merge existing preferences with default columns (this handles both additions and removals)
        const mergedColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }
    
    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    // eslint-disable-next-line
  }, [dispatch, pageKey]);

  const GoToEdit = (custGuid) => navigate(`/customer/updateCustomer/${custGuid}`);

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Customer</li>
          </ol>
          <h1 className="page-header mb-0">Manage Customer</h1>
        </div>

        <div className="ms-auto">
          <Link to="/customer/AddCustomer">
            <button 
              className="btn btn-rounded px-4 rounded-pill"
              style={{ 
                backgroundColor: themeColor,
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                border: 'none',
                color: 'white',
                transition: 'all 0.3s ease',
                boxShadow: `0 2px 8px ${themeColor}40`,
                fontSize: '14px',
                fontWeight: '600'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
              }}
            >
              <i className="fa fa-plus fa-lg me-2 ms-n2" style={{ color: 'white' }}></i>
              Add Customer
            </button>
          </Link>
        </div>
      </div>
      <div className="mb-3 d-sm-flex fw-bold">
        <div className="mt-sm-0 mt-2">
          <a href="#/" className="text-dark text-decoration-none">
            <i className="fa fa-download fa-fw me-1 text-dark text-opacity-50"></i>{" "}
            Export
          </a>
        </div>
        <div className="ms-sm-4 ps-sm-1 mt-sm-0 mt-2">
          <a href="#/" className="text-dark text-decoration-none">
            <i className="fa fa-upload fa-fw me-1 text-dark text-opacity-50"></i>{" "}
            Import
          </a>
        </div>
        <div className="ms-sm-4 ps-sm-1 mt-sm-0 mt-2 dropdown-toggle">
          <a
            href="#/"
            data-bs-toggle="dropdown"
            className="text-dark text-decoration-none"
          >
            <i className="fa fa-cog fa-fw me-1 text-dark text-opacity-50"></i>{" "}
            More Actions
          </a>
          <div className="dropdown-menu">
            <a className="dropdown-item" href="#/">
              Action
            </a>
            <a className="dropdown-item" href="#/">
              Another action
            </a>
            <a className="dropdown-item" href="#/">
              Something else here
            </a>
            <div role="separator" className="dropdown-divider"></div>
            <a className="dropdown-item" href="#/">
              Separated link
            </a>
          </div>
        </div>
      </div>
      <div className="mb-3 mt-2">
        {(customers.length > 0 || search.trim() || !loading) && (
          <div className="card border-0">
            <div className="tab-content p-3">
              <div className="tab-pane fade show active" id="allTab">
                {/* Enhanced Filter Section */}
                <div className="mb-4">
                  <div
                    className="card filter-card"
                    style={{
                      position: "relative",
                      zIndex: 1,
                      background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                      borderRadius: '12px',
                      border: 'none',
                      boxShadow: `0 4px 20px ${themeColor}40`
                    }}
                  >
                    <div className="card-body p-3">
                      {/* Filter Controls */}
                      <div className="row g-3 align-items-center">
                        {/* Search Field */}
                        <div className="col-xl-4 col-lg-4 col-md-6">
                          <div className="search-input filter-control">
                            <input
                              type="text"
                              className="filter-input"
                              value={search}
                              onChange={(e) => {
                                setSearch(e.target.value);
                                setPageNumber(1);
                              }}
                              placeholder="Search by name, email, phone..."
                              style={{
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                color: '#333'
                              }}
                            />
                          </div>
                        </div>

                        {/* Sort By Dropdown */}
                        <div className="col-xl-3 col-lg-3 col-md-6">
                          <div className="filter-control">
                            <Select
                              value={sortBy}
                              onChange={(val) => {
                                dispatch(setSortBy({ pageKey, sortBy: val }));
                                setPageNumber(1);
                              }}
                              options={sortByOptions}
                              styles={dropdownStyles}
                              placeholder="Sort By"
                              className="filter-dropdown"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              menuShouldBlockScroll={false}
                              menuShouldScrollIntoView={false}
                              closeMenuOnScroll={false}
                            />
                          </div>
                        </div>

                        {/* Sort Order Dropdown */}
                        <div className="col-xl-2 col-lg-2 col-md-6">
                          <div className="filter-control">
                            <SortOrderDropDownSelection
                              defaultValue={sortOrder}
                              onChange={(val) => {
                                dispatch(setSortOrder({ pageKey, sortOrder: val }));
                                setPageNumber(1);
                              }}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                            />
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="col-xl-3 col-lg-3 col-md-12">
                          <div className="d-flex gap-2">
                            <button
                              className="clear-btn"
                              onClick={clearFilters}
                              title="Clear all filters"
                            >
                              <i className="fa fa-times me-1"></i>
                              Filter
                            </button>

                            <ColumnSelection
                              visibleColumns={visibleColumns}
                              setVisibleColumns={(cols) =>
                                dispatch(setVisibleColumns({ pageKey, columns: cols }))
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Selection Info and Actions (Shown only when customers are selected) */}
                {selectedItems.size > 0 && (
                  <div className="mb-3">
                    <div className="p-3 bg-light rounded d-flex align-items-center justify-content-between" style={{ border: "1px solid #dee2e6" }}>
                      <div className="d-flex align-items-center gap-3">
                        <span className="fw-bold text-primary">
                          {selectedItems.size} customer{selectedItems.size > 1 ? 's' : ''} selected
                        </span>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={handleDeselectAll}
                        >
                          Clear Selection
                        </button>
                      </div>

                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={handleBulkEdit}
                          title="Edit Selected Customers"
                        >
                          <i className="fa fa-edit me-1"></i>
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={handleBulkDelete}
                          title="Delete Selected Customers"
                        >
                          <i className="fa fa-trash me-1"></i>
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Empty State Message */}
                {customers.length === 0 && !loading && (
                  <div className="text-center py-5">
                    <div className="mb-4">
                      <i className="fa fa-users fa-3x text-muted opacity-50"></i>
                    </div>
                    <h5 className="text-muted mb-3">No Customers Found</h5>
                    <p className="text-muted mb-4">
                      {search.trim() 
                        ? `No customers match your search criteria "${search.trim()}"`
                        : "You haven't added any customers yet."
                      }
                    </p>
                    {!search.trim() && (
                      <Link to="/customer/AddCustomer">
                        <button 
                          className="btn px-4"
                          style={{ 
                            backgroundColor: themeColor,
                            background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                            border: 'none',
                            color: 'white',
                            borderRadius: '8px',
                            padding: '12px 24px',
                            fontSize: '14px',
                            fontWeight: '600',
                            transition: 'all 0.3s ease',
                            boxShadow: `0 2px 8px ${themeColor}40`
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
                          }}
                        >
                          <i className="fa fa-plus me-2"></i>
                          Add Your First Customer
                        </button>
                      </Link>
                    )}
                    {search.trim() && (
                      <button 
                        className="btn btn-outline-secondary"
                        onClick={clearFilters}
                      >
                        <i className="fa fa-times me-2"></i>
                        Clear Search
                      </button>
                    )}
                  </div>
                )}

                {customers.length > 0 && (
                  <div
                    className="table-responsive mt-3"
                    style={{
                      minHeight: "200px",
                      maxHeight: "450px",
                      overflowY: "auto",
                      overflowX: "auto",
                    }}
                  >
                    <table className="table table-hover">
                    <thead
                      className="bg-light"
                      style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                        backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                        color: isDarkMode ? "#ffffff" : "#000000",
                      }}
                    >
                      <tr>
                        <th className="pt-2 pb-2">
                          <input
                            type="checkbox"
                            onChange={handleSelectAll}
                            checked={
                              customers.length > 0 &&
                              selectedItems.size === customers.length
                            }
                          />
                        </th>
                        <th className="pt-2 pb-2">Customer Name</th>
                        {visibleColumns.email && (
                          <th className="pt-2 pb-2 text-center">Email</th>
                        )}
                        {visibleColumns.mobileNo && (
                          <th className="pt-2 pb-2 text-center">Mobile No</th>
                        )}
                        {visibleColumns.city && (
                          <th className="pt-2 pb-2 text-center">City</th>
                        )}
                        {visibleColumns.postcode && (
                          <th className="pt-2 pb-2 text-center">Post Code</th>
                        )}
                        {visibleColumns.phone && (
                          <th className="pt-2 pb-2 text-center">Phone</th>
                        )}
                        {visibleColumns.address && (
                          <th className="pt-2 pb-2 text-center">Address</th>
                        )}
                        {visibleColumns.country && (
                          <th className="pt-2 pb-2 text-center">Country</th>
                        )}
                        {visibleColumns.totalOrders && (
                          <th className="pt-2 pb-2 text-center">Total Orders</th>
                        )}
                        {visibleColumns.totalSpent && (
                          <th className="pt-2 pb-2 text-center">Total Spent</th>
                        )}
                        {visibleColumns.averageOrderSpent && (
                          <th className="pt-2 pb-2 text-center">Avg Order Spent</th>
                        )}
                        {visibleColumns.lastOrderDate && (
                          <th className="pt-2 pb-2 text-center">Last Order Date</th>
                        )}
                        {visibleColumns.currentBalance && (
                          <th className="pt-2 pb-2 text-center">On Account</th>
                        )}
                        {visibleColumns.lastOrderType && (
                          <th className="pt-2 pb-2 text-center">Last Order Type</th>
                        )}
                        {visibleColumns.totalEarnedLoyaltyPoints && (
                          <th className="pt-2 pb-2 text-center">Total Earned Points</th>
                        )}
                        {visibleColumns.totalSpentLoyaltyPoints && (
                          <th className="pt-2 pb-2 text-center">Total Spent Points</th>
                        )}
                        {visibleColumns.deposit && (
                          <th className="pt-2 pb-2 text-center">Store Credit</th>
                        )}
                        {visibleColumns.maxCredit && (
                          <th className="pt-2 pb-2 text-center">Max Credit</th>
                        )}
                        {visibleColumns.referenceID && (
                          <th className="pt-2 pb-2 text-center">Reference ID</th>
                        )}
                        {visibleColumns.vatNumber && (
                          <th className="pt-2 pb-2 text-center">VAT Number</th>
                        )}
                        {visibleColumns.businessName && (
                          <th className="pt-2 pb-2 text-center">Business Name</th>
                        )}
                        {visibleColumns.dateOfBirth && (
                          <th className="pt-2 pb-2 text-center">Date of Birth</th>
                        )}
                        {visibleColumns.gender && (
                          <th className="pt-2 pb-2 text-center">Gender</th>
                        )}
                        {visibleColumns.title && (
                          <th className="pt-2 pb-2 text-center">Title</th>
                        )}
                        <th className="pt-2 pb-2 text-center">Actions</th>
                      </tr>
                    </thead>

                    <tbody>
                      {customers.map((customer, index) => (
                        <tr key={customer.cust_Guid}>
                          <td className="align-middle">
                            <input
                              type="checkbox"
                              checked={selectedItems.has(customer.cust_Guid)}
                              onChange={(e) =>
                                handleSelectItem(customer.cust_Guid, index, e)
                              }
                              data-chkbox-shiftsel="type1"
                            />
                          </td>
                          <td
                            className="align-middle"
                            onClick={() => {
                              GoToEdit(customer.cust_Guid);
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            {customer.cust_Fname + " " + customer.cust_Lname}
                          </td>
                          {visibleColumns.email && (
                            <td
                              className="align-middle text-center"
                              onClick={() => {
                                GoToEdit(customer.cust_Guid);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cust_Email}
                            </td>
                          )}
                          {visibleColumns.mobileNo && (
                            <td
                              className="align-middle text-center"
                              onClick={() => {
                                GoToEdit(customer.cust_Guid);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cust_Mobile}
                            </td>
                          )}
                          {visibleColumns.city && (
                            <td
                              className="align-middle text-center"
                              onClick={() => {
                                GoToEdit(customer.cust_Guid);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cust_City}
                            </td>
                          )}
                          {visibleColumns.postcode && (
                            <td
                              className="align-middle text-center"
                              onClick={() => {
                                GoToEdit(customer.cust_Guid);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cust_PostCode || '-'}
                            </td>
                          )}
                          {visibleColumns.phone && (
                            <td
                              className="align-middle text-center"
                              onClick={() => {
                                GoToEdit(customer.cust_Guid);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cust_Phone || '-'}
                            </td>
                          )}
                          {visibleColumns.address && (
                            <td
                              className="align-middle text-center"
                              onClick={() => {
                                GoToEdit(customer.cust_Guid);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cust_Address || '-'}
                            </td>
                          )}
                          {visibleColumns.country && (
                            <td
                              className="align-middle text-center"
                              onClick={() => {
                                GoToEdit(customer.cust_Guid);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cust_Country || '-'}
                            </td>
                          )}
                          {visibleColumns.totalOrders && (
                            <td
                              className="align-middle text-center"
                              onClick={() => {
                                GoToEdit(customer.cust_Guid);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cust_TotalOrderCount || 0}
                            </td>
                          )}
                          {visibleColumns.totalSpent && (
                            <td
                              className="align-middle text-center"
                              onClick={() => {
                                GoToEdit(customer.cust_Guid);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cust_TotalOrderSpent ? `${getCurrencySymbol(compCurrency)}${parseFloat(customer.cust_TotalOrderSpent).toFixed(2)}` : `${getCurrencySymbol(compCurrency)}0.00`}
                            </td>
                          )}
                          {visibleColumns.averageOrderSpent && (
                            <td
                              className="align-middle text-center"
                              onClick={() => {
                                GoToEdit(customer.cust_Guid);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cust_AverageOrderSpent ? `${getCurrencySymbol(compCurrency)}${parseFloat(customer.cust_AverageOrderSpent).toFixed(2)}` : `${getCurrencySymbol(compCurrency)}0.00`}
                            </td>
                          )}
                          {visibleColumns.lastOrderDate && (
                            <td
                              className="align-middle text-center"
                              onClick={() => {
                                GoToEdit(customer.cust_Guid);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cus_LastOrderDate ? new Date(customer.cus_LastOrderDate).toLocaleDateString() : 'Never'}
                            </td>
                          )}
                          {visibleColumns.currentBalance && (
                            <td
                              className="align-middle text-center"
                              onClick={() => {
                                GoToEdit(customer.cust_Guid);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cust_CurrentBalance ? `${getCurrencySymbol(compCurrency)}${parseFloat(customer.cust_CurrentBalance).toFixed(2)}` : `${getCurrencySymbol(compCurrency)}0.00`}
                            </td>
                          )}
                          {visibleColumns.lastOrderType && (
                            <td
                              className="align-middle text-center"
                              onClick={() => {
                                GoToEdit(customer.cust_Guid);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cust_LastOrderType || '-'}
                            </td>
                          )}
                          {visibleColumns.totalEarnedLoyaltyPoints && (
                            <td
                              className="align-middle text-center"
                              onClick={() => {
                                GoToEdit(customer.cust_Guid);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cust_TotalEarnedLoyaltyPoints || 0}
                            </td>
                          )}
                          {visibleColumns.totalSpentLoyaltyPoints && (
                            <td
                              className="align-middle text-center"
                              onClick={() => {
                                GoToEdit(customer.cust_Guid);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cust_TotalSpentLoyaltyPoints || 0}
                            </td>
                          )}
                          {visibleColumns.deposit && (
                            <td
                              className="align-middle text-center"
                              onClick={() => {
                                GoToEdit(customer.cust_Guid);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cust_Deposit ? `${getCurrencySymbol(compCurrency)}${parseFloat(customer.cust_Deposit).toFixed(2)}` : `${getCurrencySymbol(compCurrency)}0.00`}
                            </td>
                          )}
                          {visibleColumns.maxCredit && (
                            <td
                              className="align-middle text-center"
                              onClick={() => {
                                GoToEdit(customer.cust_Guid);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cust_MaxCredit ? `${getCurrencySymbol(compCurrency)}${parseFloat(customer.cust_MaxCredit).toFixed(2)}` : `${getCurrencySymbol(compCurrency)}0.00`}
                            </td>
                          )}
                          {visibleColumns.referenceID && (
                            <td
                              className="align-middle text-center"
                              onClick={() => {
                                GoToEdit(customer.cust_Guid);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cust_Reference_ID || '-'}
                            </td>
                          )}
                          {visibleColumns.vatNumber && (
                            <td
                              className="align-middle text-center"
                              onClick={() => {
                                GoToEdit(customer.cust_Guid);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cust_VAT_Number || '-'}
                            </td>
                          )}
                          {visibleColumns.businessName && (
                            <td
                              className="align-middle text-center"
                              onClick={() => {
                                GoToEdit(customer.cust_Guid);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cust_BusinessName || '-'}
                            </td>
                          )}
                          {visibleColumns.dateOfBirth && (
                            <td
                              className="align-middle text-center"
                              onClick={() => {
                                GoToEdit(customer.cust_Guid);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cust_DOB ? new Date(customer.cust_DOB).toLocaleDateString() : '-'}
                            </td>
                          )}
                          {visibleColumns.gender && (
                            <td
                              className="align-middle text-center"
                              onClick={() => {
                                GoToEdit(customer.cust_Guid);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cust_Gender || '-'}
                            </td>
                          )}
                          {visibleColumns.title && (
                            <td
                              className="align-middle text-center"
                              onClick={() => {
                                GoToEdit(customer.cust_Guid);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cust_Title || '-'}
                            </td>
                          )}
                          <td className="align-middle text-center">
                            <div className="d-flex justify-content-center gap-2">
                              {/* Edit Icon */}
                              <Link
                                to={`/customer/updateCustomer/${customer.cust_Guid}`}
                                className="btn btn-sm btn-outline-primary"
                                title="Edit Customer"
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '6px',
                                  border: `1px solid ${themeColor}`,
                                  color: themeColor,
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = themeColor;
                                  e.currentTarget.style.color = 'white';
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                  e.currentTarget.style.color = themeColor;
                                  e.currentTarget.style.transform = 'scale(1)';
                                }}
                              >
                                <i className="fa fa-edit" style={{ fontSize: '12px' }}></i>
                              </Link>

                              <Link
                                to={`/customer/wallet/${customer.cust_Guid}`}
                                className="btn btn-sm btn-outline-success"
                                title="Wallet / Ledger"
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '6px',
                                  border: '1px solid #198754',
                                  color: '#198754',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#198754';
                                  e.currentTarget.style.color = 'white';
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                  e.currentTarget.style.color = '#198754';
                                  e.currentTarget.style.transform = 'scale(1)';
                                }}
                              >
                                <i className="fa fa-wallet" style={{ fontSize: '12px' }}></i>
                              </Link>

                              {/* Delete Icon */}
                              <button
                                onClick={() => DeleteCustomer(customer)}
                                className="btn btn-sm btn-outline-danger"
                                title="Delete Customer"
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '6px',
                                  border: '1px solid #dc3545',
                                  color: '#dc3545',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#dc3545';
                                  e.currentTarget.style.color = 'white';
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                  e.currentTarget.style.color = '#dc3545';
                                  e.currentTarget.style.transform = 'scale(1)';
                                }}
                              >
                                <i className="fa fa-trash" style={{ fontSize: '12px' }}></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                )}
                
                <Pagination
                  totalRecords={totalRecords}
                  pageSize={pageSize}
                  pageNumber={pageNumber}
                  totalPages={totalPages}
                  setPageNumber={setPageNumber}
                  setPageSize={setPageSize}
                  themeColor={themeColor}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageCustomers;